import React from 'react';
import { FileText, Download, Calendar, BarChart3, Printer, Building2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface Site {
  id: string;
  name: string;
  type: string;
  address: string;
  start_date: string;
  end_date: string;
  description?: string;
  requirements?: string[];
  status: string;
  asbestos_type?: string;
}

interface Worker {
  id: string;
  name: string;
  role: string;
  email: string;
  phone?: string;
  certifications?: string[];
  whitecard_number?: string;
}

interface Induction {
  id: string;
  site_id: string;
  worker_id: string;
  status: string;
  completed_at?: string;
  created_at: string;
  signature?: string;
  worker?: Worker;
}

interface SWMS {
  id: string;
  title: string;
  site_id: string;
  activity_description: string;
  approval_status: string;
  created_at: string;
  version: number;
}

interface SWMSSignature {
  id: string;
  swms_id: string;
  worker_id: string;
  signature?: string;
  signed_at?: string;
  acknowledged: boolean;
  worker?: Worker;
}

interface SiteComplianceData {
  site: Site;
  inductions: Induction[];
  swms: SWMS[];
  swmsSignatures: SWMSSignature[];
  completedInductions: number;
  pendingInductions: number;
  totalWorkers: number;
}

export const ReportsPage: React.FC = () => {
  const [generatingReport, setGeneratingReport] = React.useState(false);

  const generateSafetyComplianceReport = async () => {
    setGeneratingReport(true);
    try {
      // Fetch all sites
      const { data: sitesData, error: sitesError } = await supabase
        .from('sites')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (sitesError) throw sitesError;

      const siteComplianceData: SiteComplianceData[] = [];

      // For each site, fetch inductions and SWMS data
      for (const site of sitesData || []) {
        // Fetch inductions for this site
        const { data: inductionsData, error: inductionsError } = await supabase
          .from('inductions')
          .select(`
            *,
            workers!inner(id, name, role, email, phone, certifications, whitecard_number)
          `)
          .eq('site_id', site.id)
          .order('created_at', { ascending: false });

        if (inductionsError) throw inductionsError;

        // Fetch SWMS for this site
        const { data: swmsData, error: swmsError } = await supabase
          .from('swms')
          .select('*')
          .eq('site_id', site.id)
          .order('created_at', { ascending: false });

        if (swmsError) throw swmsError;

        // Fetch SWMS signatures for this site
        let swmsSignatures: SWMSSignature[] = [];
        if (swmsData && swmsData.length > 0) {
          const swmsIds = swmsData.map(s => s.id);
          const { data: signaturesData, error: signaturesError } = await supabase
            .from('swms_signatures')
            .select(`
              *,
              workers!inner(id, name, role, email, phone, certifications, whitecard_number)
            `)
            .in('swms_id', swmsIds)
            .eq('acknowledged', true)
            .order('signed_at', { ascending: false });

          if (signaturesError) throw signaturesError;
          swmsSignatures = signaturesData || [];
        }

        const inductions = (inductionsData || []).map(induction => ({
          ...induction,
          worker: induction.workers
        }));

        const completedInductions = inductions.filter(i => i.status === 'completed').length;
        const pendingInductions = inductions.filter(i => i.status === 'pending').length;
        const totalWorkers = inductions.length;

        siteComplianceData.push({
          site,
          inductions,
          swms: swmsData || [],
          swmsSignatures: swmsSignatures.map(sig => ({
            ...sig,
            worker: sig.workers
          })),
          completedInductions,
          pendingInductions,
          totalWorkers
        });
      }

      // Generate PDF
      await generateCompliancePDF(siteComplianceData);

    } catch (error) {
      console.error('Error generating safety compliance report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateCompliancePDF = async (siteData: SiteComplianceData[]) => {
    // Create a temporary container for the PDF content
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '794px'; // A4 width in pixels at 96 DPI
    container.style.backgroundColor = 'white';
    container.style.padding = '30px';
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.lineHeight = '1.4';
    container.style.fontSize = '12px';
    
    const totalInductions = siteData.reduce((sum, site) => sum + site.totalWorkers, 0);
    const totalCompleted = siteData.reduce((sum, site) => sum + site.completedInductions, 0);
    const totalSWMS = siteData.reduce((sum, site) => sum + site.swms.length, 0);
    const totalSWMSSignatures = siteData.reduce((sum, site) => sum + site.swmsSignatures.length, 0);
    
    // Create the HTML content
    container.innerHTML = `
      <div style="max-width: 734px; margin: 0 auto;">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 25px; border-bottom: 3px solid #ea580c; padding-bottom: 20px;">
          <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 15px;">
            <div style="width: 50px; height: 50px; background-color: #fed7aa; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-right: 15px;">
              <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <div>
              <h1 style="font-size: 26px; font-weight: bold; color: #1f2937; margin: 0; line-height: 1.1;">SAFETY COMPLIANCE REPORT</h1>
              <p style="font-size: 14px; color: #6b7280; margin: 5px 0 0 0;">Comprehensive Safety Induction & SWMS Compliance</p>
            </div>
          </div>
        </div>

        <!-- Executive Summary -->
        <div style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; border-radius: 10px; padding: 20px; margin-bottom: 25px;">
          <h2 style="font-size: 20px; font-weight: bold; margin: 0 0 15px 0;">Executive Summary</h2>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px;">
            <div style="text-align: center; background-color: rgba(255,255,255,0.1); border-radius: 8px; padding: 12px;">
              <p style="font-size: 24px; font-weight: bold; margin: 0; line-height: 1;">${siteData.length}</p>
              <p style="font-size: 11px; margin: 0; opacity: 0.9;">Active Sites</p>
            </div>
            <div style="text-align: center; background-color: rgba(255,255,255,0.1); border-radius: 8px; padding: 12px;">
              <p style="font-size: 24px; font-weight: bold; margin: 0; line-height: 1;">${totalCompleted}</p>
              <p style="font-size: 11px; margin: 0; opacity: 0.9;">Completed Inductions</p>
            </div>
            <div style="text-align: center; background-color: rgba(255,255,255,0.1); border-radius: 8px; padding: 12px;">
              <p style="font-size: 24px; font-weight: bold; margin: 0; line-height: 1;">${totalSWMS}</p>
              <p style="font-size: 11px; margin: 0; opacity: 0.9;">SWMS Documents</p>
            </div>
            <div style="text-align: center; background-color: rgba(255,255,255,0.1); border-radius: 8px; padding: 12px;">
              <p style="font-size: 24px; font-weight: bold; margin: 0; line-height: 1;">${Math.round((totalCompleted / Math.max(totalInductions, 1)) * 100)}%</p>
              <p style="font-size: 11px; margin: 0; opacity: 0.9;">Compliance Rate</p>
            </div>
          </div>
        </div>

        <!-- Site-by-Site Compliance -->
        ${siteData.map((siteInfo, siteIndex) => `
          <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin-bottom: 25px; page-break-inside: avoid;">
            <!-- Site Header -->
            <div style="background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); color: white; border-radius: 8px; padding: 15px; margin-bottom: 20px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <h3 style="font-size: 18px; font-weight: bold; margin: 0 0 5px 0;">${siteInfo.site.name}</h3>
                  <p style="font-size: 12px; margin: 0; opacity: 0.9;">📍 ${siteInfo.site.address}</p>
                  <p style="font-size: 11px; margin: 0; opacity: 0.8; text-transform: capitalize;">
                    ${siteInfo.site.type.replace('_', ' ')} Project
                  </p>
                </div>
                <div style="text-align: right;">
                  <div style="background-color: rgba(255,255,255,0.2); border-radius: 6px; padding: 8px;">
                    <p style="font-size: 16px; font-weight: bold; margin: 0; line-height: 1;">Site ${siteIndex + 1}</p>
                    <p style="font-size: 10px; margin: 0; opacity: 0.9;">of ${siteData.length}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Site Statistics -->
            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px;">
              <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; text-align: center;">
                <p style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0;">${siteInfo.totalWorkers}</p>
                <p style="font-size: 10px; color: #6b7280; margin: 0;">Total Workers</p>
              </div>
              <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; text-align: center;">
                <p style="font-size: 18px; font-weight: bold; color: #16a34a; margin: 0;">${siteInfo.completedInductions}</p>
                <p style="font-size: 10px; color: #6b7280; margin: 0;">Completed Inductions</p>
              </div>
              <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; text-align: center;">
                <p style="font-size: 18px; font-weight: bold; color: #3b82f6; margin: 0;">${siteInfo.swms.length}</p>
                <p style="font-size: 10px; color: #6b7280; margin: 0;">SWMS Documents</p>
              </div>
              <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; text-align: center;">
                <p style="font-size: 18px; font-weight: bold; color: #7c3aed; margin: 0;">${siteInfo.swmsSignatures.length}</p>
                <p style="font-size: 10px; color: #6b7280; margin: 0;">SWMS Signatures</p>
              </div>
            </div>

            ${siteInfo.site.requirements && siteInfo.site.requirements.length > 0 ? `
              <!-- Site Requirements -->
              <div style="background-color: #fef3c7; border: 1px solid #f59e0b; border-radius: 6px; padding: 12px; margin-bottom: 15px;">
                <h4 style="font-size: 12px; font-weight: 600; color: #92400e; margin: 0 0 8px 0;">Site Safety Requirements</h4>
                <p style="font-size: 10px; color: #92400e; margin: 0; line-height: 1.4;">
                  ${siteInfo.site.requirements.join(' • ')}
                </p>
              </div>
            ` : ''}

            <!-- Completed Inductions -->
            ${siteInfo.completedInductions > 0 ? `
              <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 15px; margin-bottom: 15px;">
                <h4 style="font-size: 14px; font-weight: 600; color: #16a34a; margin: 0 0 12px 0;">✅ Completed Inductions (${siteInfo.completedInductions})</h4>
                <div style="display: grid; gap: 8px;">
                  ${siteInfo.inductions.filter(i => i.status === 'completed').slice(0, 10).map((induction, index) => `
                    <div style="background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px; display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center;">
                      <div style="width: 20px; height: 20px; background-color: #dcfce7; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                        <span style="font-size: 9px; font-weight: bold; color: #166534;">${index + 1}</span>
                      </div>
                      <div>
                        <p style="font-size: 11px; font-weight: 600; color: #1f2937; margin: 0;">${induction.worker?.name || 'Unknown Worker'}</p>
                        <p style="font-size: 9px; color: #6b7280; margin: 0; text-transform: capitalize;">${induction.worker?.role?.replace('_', ' ') || 'N/A'}</p>
                      </div>
                      <div style="text-align: right;">
                        <p style="font-size: 9px; color: #374151; margin: 0;">${induction.completed_at ? new Date(induction.completed_at).toLocaleDateString() : 'N/A'}</p>
                        ${induction.signature ? '<p style="font-size: 8px; color: #16a34a; margin: 0;">✓ Signed</p>' : ''}
                      </div>
                    </div>
                  `).join('')}
                  ${siteInfo.completedInductions > 10 ? `
                    <div style="text-align: center; padding: 8px;">
                      <p style="font-size: 10px; color: #6b7280; margin: 0;">... and ${siteInfo.completedInductions - 10} more completed inductions</p>
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}

            <!-- SWMS Acknowledgments -->
            ${siteInfo.swmsSignatures.length > 0 ? `
              <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 15px; margin-bottom: 15px;">
                <h4 style="font-size: 14px; font-weight: 600; color: #7c3aed; margin: 0 0 12px 0;">📋 SWMS Acknowledgments (${siteInfo.swmsSignatures.length})</h4>
                <div style="display: grid; gap: 8px;">
                  ${siteInfo.swmsSignatures.slice(0, 8).map((signature, index) => `
                    <div style="background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px; display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center;">
                      <div style="width: 20px; height: 20px; background-color: #ede9fe; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                        <span style="font-size: 9px; font-weight: bold; color: #6b21a8;">${index + 1}</span>
                      </div>
                      <div>
                        <p style="font-size: 11px; font-weight: 600; color: #1f2937; margin: 0;">${signature.worker?.name || 'Unknown Worker'}</p>
                        <p style="font-size: 9px; color: #6b7280; margin: 0; text-transform: capitalize;">${signature.worker?.role?.replace('_', ' ') || 'N/A'}</p>
                      </div>
                      <div style="text-align: right;">
                        <p style="font-size: 9px; color: #374151; margin: 0;">${signature.signed_at ? new Date(signature.signed_at).toLocaleDateString() : 'N/A'}</p>
                        ${signature.signature ? '<p style="font-size: 8px; color: #7c3aed; margin: 0;">✓ Acknowledged</p>' : ''}
                      </div>
                    </div>
                  `).join('')}
                  ${siteInfo.swmsSignatures.length > 8 ? `
                    <div style="text-align: center; padding: 8px;">
                      <p style="font-size: 10px; color: #6b7280; margin: 0;">... and ${siteInfo.swmsSignatures.length - 8} more SWMS acknowledgments</p>
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}

            <!-- Pending Items -->
            ${siteInfo.pendingInductions > 0 ? `
              <div style="background-color: #fef3c7; border: 1px solid #f59e0b; border-radius: 6px; padding: 12px;">
                <h4 style="font-size: 12px; font-weight: 600; color: #92400e; margin: 0 0 8px 0;">⏳ Pending Items</h4>
                <p style="font-size: 10px; color: #92400e; margin: 0;">
                  ${siteInfo.pendingInductions} induction(s) pending completion
                </p>
              </div>
            ` : `
              <div style="background-color: #f0fdf4; border: 1px solid #22c55e; border-radius: 6px; padding: 12px;">
                <h4 style="font-size: 12px; font-weight: 600; color: #166534; margin: 0 0 8px 0;">✅ Fully Compliant</h4>
                <p style="font-size: 10px; color: #166534; margin: 0;">
                  All inductions completed for this site
                </p>
              </div>
            `}
          </div>
        `).join('')}

        <!-- Overall Compliance Summary -->
        <div style="background-color: #f0fdf4; border: 1px solid #22c55e; border-radius: 8px; padding: 18px; margin-bottom: 20px;">
          <h3 style="font-size: 16px; font-weight: 600; color: #166534; margin: 0 0 12px 0; display: flex; align-items: center;">
            <span style="margin-right: 8px;">🛡️</span> Overall Compliance Summary
          </h3>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
            <div>
              <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ ${totalCompleted} workers have completed safety inductions</p>
              <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ ${totalSWMSSignatures} SWMS acknowledgments captured</p>
              <p style="font-size: 12px; color: #166534; margin: 0; font-weight: 600;">✓ ${siteData.length} active sites under management</p>
            </div>
            <div>
              <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ Digital signatures verified and stored</p>
              <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ Safety procedures documented and acknowledged</p>
              <p style="font-size: 12px; color: #166534; margin: 0; font-weight: 600;">✓ Compliance records maintained for audit</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div style="text-align: center; padding-top: 20px; border-top: 2px solid #e5e7eb;">
          <p style="font-size: 11px; color: #6b7280; margin: 0 0 5px 0; line-height: 1.4;">
            <strong>Safety Compliance Report</strong><br>
            This comprehensive report covers all safety inductions and SWMS acknowledgments<br>
            across all active project sites as of the report generation date.
          </p>
          <p style="font-size: 9px; color: #9ca3af; margin: 0; line-height: 1.3;">
            Generated: ${new Date().toLocaleString()} | SafetyPortal Compliance Management System<br>
            Report covers ${siteData.length} sites, ${totalInductions} inductions, ${totalSWMS} SWMS documents
          </p>
        </div>
      </div>
    `;

    // Add the container to the document
    document.body.appendChild(container);

    try {
      // Convert the HTML to canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      const scaleX = (contentWidth * 3.78) / canvasWidth;
      const scaleY = (contentHeight * 3.78) / canvasHeight;
      const scale = Math.min(scaleX, scaleY);

      const scaledWidth = (canvasWidth * scale) / 3.78;
      const scaledHeight = (canvasHeight * scale) / 3.78;

      const xOffset = margin + (contentWidth - scaledWidth) / 2;
      const yOffset = margin;

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, scaledWidth, scaledHeight);

      // Generate filename
      const filename = `safety-compliance-report-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

    } finally {
      document.body.removeChild(container);
    }
  };

  const reportTypes = [
    {
      name: 'Safety Compliance Report',
      description: 'Complete overview of site safety compliance and induction status',
      icon: FileText,
      color: 'bg-blue-500',
    },
    {
      name: 'Worker Certification Report',
      description: 'Status of all worker certifications and expiry dates',
      icon: BarChart3,
      color: 'bg-green-500',
    },
    {
      name: 'Site Activity Report',
      description: 'Activity summary across all demolition sites',
      icon: Calendar,
      color: 'bg-orange-500',
    },
    {
      name: 'Induction Summary',
      description: 'Detailed breakdown of completed and pending inductions',
      icon: FileText,
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Calendar className="h-4 w-4" />
          <span>Data as of {new Date().toLocaleDateString()}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reportTypes.map((report) => (
          <div key={report.name} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-lg ${report.color}`}>
                <report.icon className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">{report.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{report.description}</p>
              </div>
            </div>
            <div className="mt-4 flex space-x-3">
              <button className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                <Download className="h-4 w-4" />
                <span>Download PDF</span>
              </button>
              <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                <Printer className="h-4 w-4" />
                <span>Print</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Safety Compliance Report - Special Implementation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-lg bg-blue-500">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900">Comprehensive Safety Compliance Report</h3>
            <p className="text-sm text-gray-500 mt-1">Complete overview combining inductions and SWMS signatures for all sites</p>
          </div>
        </div>
        <div className="mt-4">
          <button 
            onClick={generateSafetyComplianceReport}
            disabled={generatingReport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {generatingReport ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Generating Report...</span>
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                <span>Generate & Download PDF</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Automated Reports</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Weekly Safety Summary</p>
              <p className="text-sm text-gray-500">Automated report sent every Monday</p>
            </div>
            <div className="text-sm text-gray-500">Next: Monday, {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}</div>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Monthly Compliance Report</p>
              <p className="text-sm text-gray-500">Comprehensive monthly overview</p>
            </div>
            <div className="text-sm text-gray-500">Next: 1st of next month</div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Custom Report Builder</h4>
          <p className="text-sm text-blue-800 mb-3">
            Generate custom reports with specific date ranges and filters
          </p>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Build Custom Report
          </button>
        </div>
      </div>
    </div>
  );
};